import * as React from 'react';

import { Tooltip, Typography } from 'antd';

import addDays from 'date-fns/addDays';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import startOfDay from 'date-fns/startOfDay';
import { Field, Form } from 'formik';
import type { FormikProps } from 'formik';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { parseDdMmYyyy } from 'v2/formatters/date';
import { useUsersModifiedProduct } from 'v2/hooks/useUsersModifiedProduct';

import { CHANGE_HISTORY_FEATURE_RELEASE_DATE, calendarPreset } from '../../constants';
import type { ChangeHistoryFilterFormValues, ProductData } from '../../constants';

// TODO: migrate to react-hook-form while preserving the filter contract.

interface ChangeHistoryFilterFormProps extends FormikProps<ChangeHistoryFilterFormValues> {
  setFilters: (filter: ChangeHistoryFilterFormValues) => void;
  productData: ProductData;
}

function ChangeHistoryFilterForm({
  values,
  errors,
  setFieldValue,
  setFilters,
  productData
}: ChangeHistoryFilterFormProps) {
  const [editedAtDate, editedAtFinalDate] = values.dates;

  const featureRelease = parseDdMmYyyy(CHANGE_HISTORY_FEATURE_RELEASE_DATE);

  const isOutsideRange = (day: Date) => {
    const d = startOfDay(day);
    const isAfterToday = isAfter(d, startOfDay(new Date()));
    const rangeEnd = editedAtDate ? addDays(parseDdMmYyyy(editedAtDate), 180) : null;
    const isAfterMaxRange = rangeEnd ? isAfter(d, startOfDay(rangeEnd)) : false;
    const isBeforeFeatureRelease = isBefore(d, startOfDay(featureRelease));

    return isAfterToday || isAfterMaxRange || isBeforeFeatureRelease;
  };

  const userModifiedProductQuery = useUsersModifiedProduct({
    productId: productData.id,
    filter: {
      edited_at: editedAtDate,
      edited_at_final: editedAtFinalDate
    }
  });

  const userModifierOptions = userModifiedProductQuery.data?.map(user => ({ label: user.name, value: user.id })) || [];

  React.useEffect(() => {
    const isFirstDateBeforeFeatureRelease = isBefore(parseDdMmYyyy(editedAtDate), startOfDay(featureRelease));

    if (isFirstDateBeforeFeatureRelease)
      setFieldValue('dates', [CHANGE_HISTORY_FEATURE_RELEASE_DATE, editedAtFinalDate]);

    const isMaxRangeExceeded = isAfter(parseDdMmYyyy(editedAtFinalDate), addDays(parseDdMmYyyy(editedAtDate), 179));

    if (!isMaxRangeExceeded) {
      setFilters(values);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, errors]);

  return (
    <Form>
      <div className='mx-auto grid max-w-225 grid-cols-1 gap-4 sm:grid-cols-2'>
        <Field
          id='user_modifier'
          name='user_modifier'
          displayEmpty={true}
          options={[{ label: 'Busque por pessoa', value: '', disabled: true }, ...userModifierOptions]}
          variant='outlined'
          loading={userModifiedProductQuery.isLoading}
          component={SelectFormField}
          fullWidth
        />
        <Tooltip
          title={`Apenas serão exibidos registros de alterações realizadas a partir de ${CHANGE_HISTORY_FEATURE_RELEASE_DATE}`}
          placement='top'
        >
          <div>
            <Field
              id='calendar'
              label=''
              name='dates'
              customPreset={calendarPreset}
              component={DateRangePickerFormField}
              isOutsideRange={isOutsideRange}
              maxDate={new Date()}
            />
            {errors.dates && <Typography.Text type='danger'>{errors.dates}</Typography.Text>}
          </div>
        </Tooltip>
      </div>
    </Form>
  );
}

export { ChangeHistoryFilterForm };
